// Libraries
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// Store
import { RootState } from '../../app/store';

// API
import { fetchSessions } from './lobbyAPI';

// Models
import { SessionList, LobbyApiReturn } from './lobbyModels';

/**
 * @interface LobbyState
 * Describes the properties for the global lobby state
 *
 * @property {array} activeSessions Currently running sessions for lobby
 * @property {array} promoSessions Upcoming promoted sessions
 * @property {array} prizeSessions Upcoming non-points based prize sessions
 * @property {string} status Current data fetching status
 */
export interface LobbyState {
    activeSessions: SessionList;
    promoSessions: SessionList;
    prizeSessions: SessionList;
    error: string;
    status: 'idle' | 'loading' | 'failed';
}

/**
 * @const initialState
 * Describes the initial lobby state on application start up
 */
const initialState: LobbyState = {
    activeSessions: [],
    promoSessions: [],
    prizeSessions: [],
    error: '',
    status: 'idle',
};

/**
 * @function fetchSessionsAsync
 * Fetches current active, promoted and prize sessions from API
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @param {number} userId The current user's ID to authenticate
 *
 * @return {object} Object containing active, promoted and prize session arrays
 */
export const fetchSessionsAsync = createAsyncThunk<
    // Return type of the payload creator
    LobbyApiReturn,
    // First argument to the payload creator
    void,
    // Optional fields for defining thunkApi field types
    { rejectValue: string }
>('lobby/fetchSessions', async (_, { rejectWithValue }) => {
    try {
        const response = await fetchSessions();
        return response;
    } catch (error: any) {
        return rejectWithValue(error);
    }
});

/**
 * @const lobbySlice
 * Describes initial state and reducer functions to handle lobby state changes
 * https://redux-toolkit.js.org/api/createslice
 */
export const lobbySlice = createSlice({
    name: 'lobby',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchSessionsAsync.pending, (state) => {
                state.status = 'loading';
                state.error = '';
            })
            .addCase(fetchSessionsAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.error = '';
                state.activeSessions = action.payload.alive_sessions;
                state.promoSessions = action.payload.promo_sessions;
                state.prizeSessions = action.payload.prize_sessions;
            })
            .addCase(fetchSessionsAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload
                    ? action.payload
                    : 'We are currently experiencing issues';
            });
    },
});

/**
 * @const selectActiveSessions
 * Exposes active sessions in lobby state for use in rest of application
 */
export const selectActiveSessions = (state: RootState) =>
    state.lobby.activeSessions;

/**
 * @const selectPromoSessions
 * Exposes promoted sessions in lobby state for use in rest of application
 */
export const selectPromoSessions = (state: RootState) =>
    state.lobby.promoSessions;

/**
 * @const selectPrizeSessions
 * Exposes non-points based prize sessions in lobby state for use in rest of application
 */
export const selectPrizeSessions = (state: RootState) =>
    state.lobby.prizeSessions;

/**
 * @const selectLobbyStatus
 * Exposes current fetch status of lobby data for use in rest of application
 */
export const selectLobbyStatus = (state: RootState) => state.lobby.status;

/**
 * @const selectLobbyError
 * Exposes current fetch error of lobby data for use in rest of application
 */
export const selectLobbyError = (state: RootState) => state.lobby.error;

/**
 * @const lobbySlice.reducer
 * Exposes lobby reducer for use in store
 */
export default lobbySlice.reducer;
